import type { DivisionInfo } from '@/types/api';

export const ALL_GAME_TABS = ['8ball', '9ball', '10ball'] as const;

export type GameTabKey = (typeof ALL_GAME_TABS)[number];

export function inferAvailableGameTabs(divisionName?: string | null): GameTabKey[] {
  if (!divisionName) return [...ALL_GAME_TABS];

  // Strip the quoted venue nickname to avoid false matches on the game type.
  // e.g. "LEGEND'S LAGGER'S CHOICE" 8-ball League → the LC is in the venue, not the game type.
  const suffix = divisionName.replace(/"[^"]*"/g, '').toLowerCase();
  if (/\blc\b|lagger/.test(suffix) || /no limit lc/.test(suffix)) {
    return [...ALL_GAME_TABS];
  }
  if (suffix.includes('10-ball') || suffix.includes('10 ball')) return ['10ball'];
  if (suffix.includes('9-ball') || suffix.includes('9 ball')) return ['9ball'];
  if (/8[- ]?ball/.test(suffix)) return ['8ball'];

  return [...ALL_GAME_TABS];
}

export function getDivisionInfoById(
  divisions: DivisionInfo[] | undefined,
  divisionId?: string
): DivisionInfo | undefined {
  if (!divisions || !divisionId) return undefined;
  return divisions.find((division) => String(division.division_id) === String(divisionId));
}

/**
 * Extract a clean, short display name from the verbose NAPA division name.
 * "Thursday "LEGEND'S" 8-ball League" → "Legend's 8-Ball"
 * "Thursday "LEGEND'S" No Limit LC League" → "Legend's LC"
 */
export function cleanDivisionName(name: string | null): string {
  if (!name) return '';
  // Extract the venue nickname from quotes
  const quoteMatch = name.match(/"([^"]+)"/);
  const venue = quoteMatch
    ? quoteMatch[1]
        .split(/\s+/)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
        .replace(/['']s$/, "'s")
    : '';

  // Determine game type from the suffix (outside quotes)
  const suffix = name.replace(/"[^"]*"/g, '').toLowerCase();
  let gameType = '';
  if (/\blc\b|lagger/.test(suffix)) gameType = 'LC';
  else if (/10[- ]?ball/.test(suffix)) gameType = '10-Ball';
  else if (/9[- ]?ball/.test(suffix)) gameType = '9-Ball';
  else if (/8[- ]?ball/.test(suffix)) gameType = '8-Ball';

  return [venue, gameType].filter(Boolean).join(' ') || name;
}
